import { useState, useCallback, useEffect, useRef } from "react"
import { useNavigate, useParams, useSearch } from "@tanstack/react-router"
import { Button } from "@aix/ui"
import {
  ArrowLeft,
  Eye,
  Code,
  Palette,
  Type,
  Image,
  Save,
  Download,
  ChevronDown,
  Copy,
  Check,
  Sparkles,
  Monitor,
  Tablet,
  Smartphone,
  Loader2,
  MousePointer2,
  Plus,
} from "lucide-react"
import toast from "react-hot-toast"
import { trpc } from "../../lib/trpc"
import AuraChatSidebar from "./components/AuraChatSidebar"
import AuraPreview from "./components/AuraPreview"
import AuraCodeView from "./components/AuraCodeView"
import { captureScreenshotSimple } from "./utils/screenshotCapture"

type ViewMode = "preview" | "design" | "code"
type DeviceSize = "desktop" | "tablet" | "mobile"

interface SearchParams {
  conversationId?: string
  userMessageId?: string
  generating?: string
}

const AuraEditorScreen = () => {
  const navigate = useNavigate()
  const params = useParams({ strict: false }) as { projectId?: string; pageSlug?: string }
  const search = useSearch({ strict: false }) as SearchParams
  const projectId = params.projectId
  const pageSlugFromUrl = params.pageSlug

  // State
  const [viewMode, setViewMode] = useState<ViewMode>("preview")
  const [deviceSize, setDeviceSize] = useState<DeviceSize>("desktop")
  const [htmlContent, setHtmlContent] = useState("")
  const [isSaving, setIsSaving] = useState(false)
  const [copied, setCopied] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [isPolling, setIsPolling] = useState(search.generating === "true")
  const pollCountRef = useRef(0)
  const [isChatStreaming, setIsChatStreaming] = useState(false)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [titleValue, setTitleValue] = useState("")
  const titleInputRef = useRef<HTMLInputElement>(null)
  const MAX_POLL_ATTEMPTS = 30 // Stop after 60 seconds (30 * 2s)

  // Pages dropdown state
  const [isPagesDropdownOpen, setIsPagesDropdownOpen] = useState(false)
  const [isAddingPage, setIsAddingPage] = useState(false)
  const [newPageName, setNewPageName] = useState("")
  const pagesDropdownRef = useRef<HTMLDivElement>(null)

  // Style guide state
  const [styleGuideSource, setStyleGuideSource] = useState<{
    type: "project" | "page" | "saved" | "extract"
    pageId?: string
    pageName?: string
    guideId?: string
    guideName?: string
  }>({ type: "project" })

  // tRPC utils for cache invalidation
  const utils = trpc.useUtils()

  // Fetch project data
  const {
    data: project,
    isLoading: projectLoading,
    error: projectError,
  } = trpc.aura.getProject.useQuery({ id: projectId }, { enabled: !!projectId })

  // Get or create conversation (use from URL if provided, otherwise fetch)
  const conversationIdFromUrl = search.conversationId
  const { data: conversationFromQuery, isLoading: conversationLoading } = trpc.aura.getOrCreateConversation.useQuery(
    { projectId: projectId },
    { enabled: !!projectId && !conversationIdFromUrl },
  )

  const conversationId = conversationIdFromUrl || conversationFromQuery?.id

  // Fetch all conversations for the project
  const { data: conversationsList } = trpc.aura.listConversations.useQuery(
    { projectId: projectId, limit: 50 },
    { enabled: !!projectId },
  )

  // Create conversation mutation
  const createConversationMutation = trpc.aura.createConversation.useMutation({
    onSuccess: (newConversation) => {
      void utils.aura.listConversations.invalidate({ projectId: projectId })
      // Navigate to the new conversation
      navigate({
        to: `/aura/editor/${projectId}`,
        search: (s: SearchParams) => ({ ...s, conversationId: newConversation.id }),
        replace: true,
      })
      toast.success("New thread created")
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create thread")
    },
  })

  // Set active conversation mutation
  const setActiveConversationMutation = trpc.aura.setActiveConversation.useMutation({
    onSuccess: () => {
      void utils.aura.listConversations.invalidate({ projectId: projectId })
    },
    onError: (error) => {
      toast.error(error.message || "Failed to switch thread")
    },
  })

  // Handle conversation change
  const handleConversationChange = (newConversationId: string) => {
    setActiveConversationMutation.mutate({
      projectId: projectId,
      conversationId: newConversationId,
    })
    // Navigate to the new conversation
    navigate({
      to: `/aura/editor/${projectId}`,
      search: (s: SearchParams) => ({ ...s, conversationId: newConversationId }),
      replace: true,
    })
    // Reset HTML content for the new conversation
    setHtmlContent("")
    setIsDirty(false)
  }

  // Handle create new conversation
  const handleCreateConversation = () => {
    createConversationMutation.mutate({
      projectId: projectId,
      pageId: pageId,
      title: "New Thread",
    })
  }

  // Poll for generation status when generating
  const { data: generationStatus } = trpc.aura.getGenerationStatus.useQuery(
    {
      conversationId: conversationId,
      afterMessageId: search.userMessageId,
    },
    {
      enabled: !!conversationId && isPolling,
      refetchInterval: (data) => {
        // Stop polling when complete or failed
        if (data?.status === "completed" || data?.status === "failed") {
          console.debug("[AuraEditor] Stopping poll - status:", data?.status)
          return false
        }

        // Increment poll count (using ref to avoid re-renders)
        pollCountRef.current += 1

        console.debug("[AuraEditor] Generation status poll:", {
          status: data?.status,
          hasHtml: !!data?.html,
          isPolling,
          pollCount: pollCountRef.current,
          conversationId,
        })

        // Stop if max attempts reached
        if (pollCountRef.current >= MAX_POLL_ATTEMPTS) {
          console.debug("[AuraEditor] Stopping poll - max attempts reached")
          setIsPolling(false)
          return false
        }

        return 2000 // Poll every 2 seconds
      },
    },
  )

  // Reset poll count when polling starts
  useEffect(() => {
    if (isPolling) {
      pollCountRef.current = 0
    }
  }, [isPolling])

  // Handle poll timeout - show toast when polling stops due to max attempts
  useEffect(() => {
    if (!isPolling && pollCountRef.current >= MAX_POLL_ATTEMPTS) {
      toast.error("Generation timed out. The design may still be processing - try refreshing the page.")

      // Clear URL params
      navigate({
        to: `/aura/editor/${projectId}`,
        replace: true,
      })
    }
  }, [isPolling, projectId, navigate])

  // Get page (from URL slug or first page)
  const currentPage = pageSlugFromUrl
    ? project?.pages?.find((p) => p.slug === pageSlugFromUrl) || project?.pages?.[0]
    : project?.pages?.[0]
  const pageId = currentPage?.id

  // Redirect to first page if no pageSlug in URL (after project loads)
  useEffect(() => {
    if (project?.pages?.[0] && !pageSlugFromUrl) {
      navigate({
        to: `/aura/editor/${projectId}/${project.pages[0].slug}`,
        replace: true,
      })
    }
  }, [project?.pages, pageSlugFromUrl, projectId, navigate])

  // Create page mutation
  const createPageMutation = trpc.aura.createPage.useMutation({
    onSuccess: (newPage) => {
      void utils.aura.getProject.invalidate({ id: projectId })
      setHtmlContent("") // New page starts empty
      setIsDirty(false)
      setIsAddingPage(false)
      setNewPageName("")
      setIsPagesDropdownOpen(false)
      toast.success(`Page "${newPage.name}" created`)
      // Navigate to the new page using path-based routing
      navigate({
        to: `/aura/editor/${projectId}/${newPage.slug}`,
      })
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create page")
    },
  })

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pagesDropdownRef.current && !pagesDropdownRef.current.contains(event.target as Node)) {
        setIsPagesDropdownOpen(false)
        setIsAddingPage(false)
        setNewPageName("")
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Update page mutation
  const updatePageMutation = trpc.aura.updatePage.useMutation({
    onSuccess: () => {
      setIsDirty(false)
      void utils.aura.getProject.invalidate({ id: projectId })
    },
    onError: (error) => {
      toast.error(error.message || "Failed to save")
    },
  })

  // Update project mutation (for title editing)
  const updateProjectMutation = trpc.aura.updateProject.useMutation({
    onSuccess: () => {
      void utils.aura.getProject.invalidate({ id: projectId })
      toast.success("Project name updated")
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update project name")
    },
  })

  // Extract design system mutation
  const extractDesignSystemMutation = trpc.aura.extractDesignSystem.useMutation({
    onSuccess: (designSystem) => {
      toast.success(`Style guide "${designSystem.name}" extracted!`)
      // Optionally save it to the project
      if (designSystem.id) {
        setStyleGuideSource({
          type: "saved",
          guideId: designSystem.id,
          guideName: designSystem.name,
        })
      }
    },
    onError: (error) => {
      toast.error(error.message || "Failed to extract style guide")
    },
  })

  // Export query
  const exportQuery = trpc.aura.exportProject.useQuery(
    { projectId: projectId, format: "html-full" },
    { enabled: false },
  )

  // Upload thumbnail mutation
  const uploadThumbnailMutation = trpc.aura.uploadProjectThumbnail.useMutation({
    onSuccess: () => {
      void utils.aura.listProjects.invalidate()
      console.debug("[AuraEditor] Thumbnail uploaded successfully")
    },
    onError: (error) => {
      console.error("[AuraEditor] Failed to upload thumbnail:", error.message)
    },
  })

  // Capture and upload thumbnail (debounced to avoid multiple captures)
  const thumbnailCaptureRef = useRef<NodeJS.Timeout | null>(null)
  const captureAndUploadThumbnail = useCallback(
    (html: string) => {
      if (!projectId || !html || uploadThumbnailMutation.isLoading) return

      // Clear any pending capture
      if (thumbnailCaptureRef.current) {
        clearTimeout(thumbnailCaptureRef.current)
      }

      // Debounce the capture to avoid rapid successive captures
      thumbnailCaptureRef.current = setTimeout(() => {
        void (async () => {
          try {
            console.debug("[AuraEditor] Capturing thumbnail...")
            const base64 = await captureScreenshotSimple(html, { width: 800, height: 600 })

            if (base64) {
              console.debug("[AuraEditor] Uploading thumbnail...")
              uploadThumbnailMutation.mutate({
                projectId,
                imageBase64: base64,
                mimeType: "image/png",
              })
            }
          } catch (error) {
            console.error("[AuraEditor] Thumbnail capture failed:", error)
          }
        })()
      }, 1000) // Wait 1 second before capturing
    },
    [projectId, uploadThumbnailMutation],
  )

  // Initialize HTML content from current page (updates when pageId changes)
  useEffect(() => {
    if (currentPage) {
      setHtmlContent(currentPage.html_content || "")
      setIsDirty(false)
    }
  }, [currentPage?.id])

  // Capture thumbnail on first load if project has content but no thumbnail
  const hasAttemptedInitialCapture = useRef(false)
  useEffect(() => {
    if (
      project &&
      !project.thumbnail_url &&
      currentPage?.html_content &&
      !hasAttemptedInitialCapture.current &&
      !isPolling
    ) {
      hasAttemptedInitialCapture.current = true
      // Delay slightly to ensure content is rendered
      setTimeout(() => {
        captureAndUploadThumbnail(currentPage.html_content)
      }, 1500)
    }
  }, [project, currentPage?.html_content, isPolling, captureAndUploadThumbnail])

  // Handle generation completion
  useEffect(() => {
    if (generationStatus?.status === "completed" && generationStatus.html) {
      setHtmlContent(generationStatus.html)
      setIsPolling(false)
      toast.success("Design generated!")

      // Clear URL params
      navigate({
        to: `/aura/editor/${projectId}`,
        replace: true,
      })

      // Invalidate queries to refresh data
      void utils.aura.getProject.invalidate({ id: projectId })
      if (conversationId) {
        void utils.aura.listMessages.invalidate({ conversationId })
      }

      // Thumbnail will be captured on first load (see hasAttemptedInitialCapture effect)
    } else if (generationStatus?.status === "failed") {
      setIsPolling(false)
      toast.error(generationStatus.error || "Generation failed")

      // Clear URL params
      navigate({
        to: `/aura/editor/${projectId}`,
        replace: true,
      })
    }
  }, [generationStatus, projectId, conversationId, captureAndUploadThumbnail])

  // Handle HTML changes from chat
  const handleHtmlUpdate = useCallback((newHtml: string) => {
    setHtmlContent(newHtml)
    setIsDirty(true)
  }, [])

  // Capture thumbnail when a new version is generated (streaming ends)
  // Uses cooldown to prevent rapid captures during iterative chat
  const wasStreamingRef = useRef(false)
  const lastCaptureTimeRef = useRef(0)
  const CAPTURE_COOLDOWN_MS = 30000 // 30 seconds between captures

  useEffect(() => {
    // Detect when streaming ends with content
    if (wasStreamingRef.current && !isChatStreaming && htmlContent) {
      const now = Date.now()
      const timeSinceLastCapture = now - lastCaptureTimeRef.current

      // Only capture if cooldown has passed
      if (timeSinceLastCapture >= CAPTURE_COOLDOWN_MS) {
        lastCaptureTimeRef.current = now
        captureAndUploadThumbnail(htmlContent)
      }
    }
    wasStreamingRef.current = isChatStreaming
  }, [isChatStreaming, htmlContent, captureAndUploadThumbnail])

  // Title editing handlers
  const handleTitleClick = () => {
    setTitleValue(project?.name || "")
    setIsEditingTitle(true)
    setTimeout(() => titleInputRef.current?.focus(), 0)
  }

  const handleTitleSave = () => {
    const trimmedTitle = titleValue.trim()
    if (trimmedTitle && trimmedTitle !== project?.name) {
      updateProjectMutation.mutate({ id: projectId, name: trimmedTitle })
    }
    setIsEditingTitle(false)
  }

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleTitleSave()
    } else if (e.key === "Escape") {
      setIsEditingTitle(false)
    }
  }

  // Page switching handler
  const handlePageSelect = (page: (typeof project.pages)[0]) => {
    if (isDirty) {
      const confirm = window.confirm("You have unsaved changes. Switch page anyway?")
      if (!confirm) return
    }
    setHtmlContent(page.html_content || "")
    setIsDirty(false)
    setIsPagesDropdownOpen(false)
    // Navigate to the selected page using path-based routing
    navigate({
      to: `/aura/editor/${projectId}/${page.slug}`,
    })
  }

  // Add new page handler
  const handleAddPage = () => {
    if (!newPageName.trim()) {
      toast.error("Please enter a page name")
      return
    }
    createPageMutation.mutate({
      projectId: projectId,
      name: newPageName.trim(),
    })
  }

  // Extract style guide handler
  const handleExtractStyleGuide = () => {
    if (!htmlContent || !pageId) {
      toast.error("No content to extract style from")
      return
    }
    extractDesignSystemMutation.mutate({
      projectId: projectId,
      pageId,
      html: htmlContent,
    })
  }

  // Handlers
  const handleBack = () => {
    if (isDirty) {
      const confirm = window.confirm("You have unsaved changes. Are you sure you want to leave?")
      if (!confirm) return
    }
    navigate({ to: "/aura" })
  }

  const handleSave = async () => {
    if (!pageId) return
    setIsSaving(true)
    try {
      await updatePageMutation.mutateAsync({
        id: pageId,
        html_content: htmlContent,
      })
      toast.success("Project saved")
    } finally {
      setIsSaving(false)
    }
  }

  const handleExport = async () => {
    try {
      const result = await exportQuery.refetch()
      if (result.data) {
        await navigator.clipboard.writeText(result.data.html)
        setCopied(true)
        toast.success("HTML copied to clipboard")
        setTimeout(() => setCopied(false), 2000)
      }
    } catch (error) {
      toast.error("Failed to export")
    }
  }

  const handleDownload = async () => {
    try {
      const result = await exportQuery.refetch()
      if (result.data) {
        const blob = new Blob([result.data.html], { type: "text/html" })
        const url = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = result.data.filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        toast.success("HTML downloaded")
      }
    } catch (error) {
      toast.error("Failed to download")
    }
  }

  const getDeviceWidth = () => {
    switch (deviceSize) {
      case "mobile":
        return "375px"
      case "tablet":
        return "768px"
      default:
        return "100%"
    }
  }

  // Loading state
  if (projectLoading || conversationLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading project...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (projectError || !project) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-destructive mb-4">Failed to load project</p>
          <Button onClick={() => navigate({ to: "/aura" })}>Back to Projects</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Top Bar */}
      <div className="border-b border-border bg-background px-4 py-2 flex items-center justify-between">
        {/* Left Section */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>

          <div className="h-6 w-px bg-border" />

          {/* Project Name */}
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            {isEditingTitle ? (
              <input
                ref={titleInputRef}
                type="text"
                value={titleValue}
                onChange={(e) => setTitleValue(e.target.value)}
                onBlur={handleTitleSave}
                onKeyDown={handleTitleKeyDown}
                className="font-medium bg-transparent border-b border-primary outline-hidden px-0.5 text-foreground"
                maxLength={100}
              />
            ) : (
              <button
                onClick={handleTitleClick}
                className="font-medium hover:text-primary hover:underline cursor-pointer transition-colors"
                title="Click to edit project name"
              >
                {project.name}
              </button>
            )}
            {isDirty && <span className="text-xs text-muted-foreground">(unsaved)</span>}
            {isPolling && (
              <span className="flex items-center gap-1 text-xs text-primary">
                <Loader2 className="w-3 h-3 animate-spin" />
                Generating...
              </span>
            )}
          </div>

          <div className="h-6 w-px bg-border" />

          {/* Pages Dropdown */}
          <div className="relative" ref={pagesDropdownRef}>
            <button
              onClick={() => setIsPagesDropdownOpen(!isPagesDropdownOpen)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-md hover:bg-secondary text-sm"
            >
              <span className="text-muted-foreground">Pages -</span>
              <span className="font-medium">{currentPage?.name || "Home"}</span>
              <ChevronDown
                className={`w-3 h-3 text-muted-foreground transition-transform ${
                  isPagesDropdownOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {/* Dropdown Menu */}
            {isPagesDropdownOpen && (
              <div className="absolute top-full left-0 mt-1 w-56 bg-background border border-border rounded-lg shadow-lg z-50">
                {/* Pages List */}
                <div className="py-1 max-h-64 overflow-y-auto">
                  {project?.pages?.map((page) => (
                    <button
                      key={page.id}
                      onClick={() => handlePageSelect(page)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-secondary transition-colors ${
                        page.id === pageId ? "bg-secondary font-medium" : ""
                      }`}
                    >
                      {page.name}
                      {page.is_default && <span className="ml-2 text-xs text-muted-foreground">(default)</span>}
                    </button>
                  ))}
                </div>

                {/* Divider */}
                <div className="border-t border-border" />

                {/* Add Page Section */}
                {isAddingPage ? (
                  <div className="p-2">
                    <input
                      type="text"
                      value={newPageName}
                      onChange={(e) => setNewPageName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleAddPage()
                        if (e.key === "Escape") {
                          setIsAddingPage(false)
                          setNewPageName("")
                        }
                      }}
                      placeholder="Page name..."
                      className="w-full px-2 py-1.5 text-sm border border-border rounded bg-background focus:outline-hidden focus:ring-1 focus:ring-primary"
                      autoFocus
                    />
                    <div className="flex gap-2 mt-2">
                      <Button
                        size="sm"
                        onClick={handleAddPage}
                        disabled={createPageMutation.isLoading}
                        className="flex-1"
                      >
                        {createPageMutation.isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Add"}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setIsAddingPage(false)
                          setNewPageName("")
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setIsAddingPage(true)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary hover:bg-secondary transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Page
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Center Section - View Toggle */}
        <div className="flex items-center gap-1 p-1 bg-secondary rounded-lg">
          <button
            onClick={() => setViewMode("preview")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === "preview"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Eye className="w-4 h-4" />
            Preview
          </button>
          <button
            onClick={() => setViewMode("design")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === "design"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
            title="Click on text to edit directly"
          >
            <MousePointer2 className="w-4 h-4" />
            Design
          </button>
          <button
            onClick={() => setViewMode("code")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === "code"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Code className="w-4 h-4" />
            Code
          </button>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-2">
          {/* Device Size Toggle (only in preview/design mode) */}
          {(viewMode === "preview" || viewMode === "design") && (
            <div className="flex items-center gap-1 mr-2">
              <button
                onClick={() => setDeviceSize("desktop")}
                className={`p-1.5 rounded ${
                  deviceSize === "desktop"
                    ? "bg-secondary text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                title="Desktop"
              >
                <Monitor className="w-4 h-4" />
              </button>
              <button
                onClick={() => setDeviceSize("tablet")}
                className={`p-1.5 rounded ${
                  deviceSize === "tablet"
                    ? "bg-secondary text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                title="Tablet"
              >
                <Tablet className="w-4 h-4" />
              </button>
              <button
                onClick={() => setDeviceSize("mobile")}
                className={`p-1.5 rounded ${
                  deviceSize === "mobile"
                    ? "bg-secondary text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                title="Mobile"
              >
                <Smartphone className="w-4 h-4" />
              </button>
            </div>
          )}

          <div className="h-6 w-px bg-border" />

          {/* Tools */}
          <Button variant="ghost" size="sm" title="Fonts">
            <Type className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" title="Colors">
            <Palette className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" title="Assets">
            <Image className="w-4 h-4" />
          </Button>

          <div className="h-6 w-px bg-border" />

          {/* Actions */}
          <Button variant="ghost" size="sm" onClick={handleSave} disabled={isSaving || !isDirty}>
            {isSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            {isSaving ? "Saving..." : "Save"}
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport}>
            {copied ? (
              <>
                <Check className="w-4 h-4 mr-1" />
                Copied
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-1" />
                Copy
              </>
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownload}>
            <Download className="w-4 h-4 mr-1" />
            Download
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Chat Sidebar */}
        {conversationId && pageId && (
          <AuraChatSidebar
            projectId={projectId}
            pageId={pageId}
            conversationId={conversationId}
            currentHtml={htmlContent}
            onHtmlGenerated={handleHtmlUpdate}
            onStreamingChange={setIsChatStreaming}
            pages={project?.pages?.map((p) => ({
              id: p.id,
              name: p.name,
              slug: p.slug,
              html_content: p.html_content,
            }))}
            conversations={conversationsList?.map((c) => ({
              id: c.id,
              title: c.title,
              message_count: c.message_count,
              is_active: c.is_active,
              updated_at: c.updated_at,
            }))}
            onConversationChange={handleConversationChange}
            onCreateConversation={handleCreateConversation}
            // Style guide props
            currentPageName={currentPage?.name}
            savedStyleGuides={
              (project?.saved_style_guides as { id?: string; name?: string }[] | undefined)
                ?.filter((g): g is { id: string; name: string } => !!g.id && !!g.name)
                ?.map((g) => ({ id: g.id, name: g.name })) || []
            }
            styleGuideSource={styleGuideSource}
            onStyleGuideChange={setStyleGuideSource}
            onExtractStyleGuide={handleExtractStyleGuide}
          />
        )}

        {/* Editor/Preview Area */}
        <div className="flex-1 flex flex-col overflow-hidden bg-secondary/30">
          {/* Show generating overlay if polling */}
          {isPolling && (
            <div className="absolute inset-0 z-10 bg-background/80 flex items-center justify-center">
              <div className="text-center">
                <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
                <p className="text-lg font-medium">Generating your design...</p>
                <p className="text-sm text-muted-foreground mt-2">This usually takes 10-30 seconds</p>
              </div>
            </div>
          )}

          {viewMode === "preview" || viewMode === "design" ? (
            <AuraPreview
              html={htmlContent}
              deviceWidth={getDeviceWidth()}
              isStreaming={isChatStreaming}
              editMode={viewMode === "design" ? "text" : "off"}
              onHtmlChange={(newHtml, changeDescription) => {
                setHtmlContent(newHtml)
                setIsDirty(true)
                if (changeDescription) {
                  toast.success(changeDescription)
                }
              }}
              conversationId={conversationId}
            />
          ) : (
            // Split view: Code on left, Preview on right
            <div className="flex-1 flex overflow-hidden">
              <div className="w-1/2 border-r border-border overflow-hidden">
                <AuraCodeView
                  html={htmlContent}
                  onChange={(newHtml) => {
                    setHtmlContent(newHtml)
                    setIsDirty(true)
                  }}
                />
              </div>
              <div className="w-1/2 overflow-hidden">
                <AuraPreview html={htmlContent} deviceWidth="100%" isStreaming={isChatStreaming} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AuraEditorScreen
